import { useEffect, useMemo, useRef, useState } from 'react';
import { SITE_TYPE_OPTIONS_PATH, Size, searchWithin, t, type SearchFn, type SiteTypeOptionsAnswer, type SuggestionRow } from '@bms2/shared';
import { Dropdown } from '../../components/controls';
import { useApi } from './api';
import { useAddNew } from './AddNewHost';
import styles from './RecordPage.module.css';

/**
 * 🔒 The Site type field (piece 10; decisions 10100, 10500; T3576): the Dropdown over the active types in their order,
 * under None, ending in the Add New Site Type row, which opens the Site Types page's own add window stacked over the
 * window and picks the saved type. + Add Site and the Site's Edit form both draw it. 🔨 The row stands only where the
 * options route answers `mayAdd`, the list's own write; any other viewer picks from the list and sees no row.
 */
export interface SiteTypeFieldProps {
  label: string;
  /** The held type's id, or empty for none. */
  value: string | null;
  /** The held type's name where the list may not carry it: a site keeping a retired type reads it chosen (decision 10100). */
  valueLabel?: string | null;
  onChange: (siteTypeId: string | null) => void;
  apiBaseUrl?: string;
  /** False while the window that holds the field is closed, so nothing is read. */
  enabled?: boolean;
  /** The hook a page and a suite find the field by. */
  dataAttribute: Readonly<Record<string, string>>;
}

export function SiteTypeField({ label, value, valueLabel = null, onChange, apiBaseUrl = '', enabled = true, dataAttribute }: SiteTypeFieldProps) {
  const read = useApi<SiteTypeOptionsAnswer>(enabled ? SITE_TYPE_OPTIONS_PATH : null, apiBaseUrl);
  const addNew = useAddNew(apiBaseUrl);
  /** The name picked in this window, which a type added from the row carries before the list is read again. */
  const [picked, setPicked] = useState<string | null>(null);
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  // 🔨 An answer missing its list reads as no types, never a crash of the window.
  const rows = useMemo(() => (Array.isArray(answer?.rows) ? answer.rows : []), [answer]);
  const search = useMemo(() => searchWithin(rows.map((row) => ({ id: row.value, line1: row.label })), [{ id: '', line1: t('edit.none') }]), [rows]);
  // 🔨 T3583: the Dropdown searches when it opens, so a field opened before its list is read waits here for the answer rather than listing None alone.
  // 🔨 T3597: a disabled field reads nothing, so it counts as settled and parks no search that nothing would ever release.
  const settled = !enabled || read.state.kind === 'ready' || read.state.kind === 'failed';
  const latest = useRef(search);
  const arrived = useRef(settled);
  const waiting = useRef<Array<() => void>>([]);
  useEffect(() => {
    latest.current = search;
    arrived.current = settled;
    if (!settled) return;
    for (const release of waiting.current) release();
    waiting.current = [];
  }, [settled, search]);
  const searchWhenRead = useMemo<SearchFn>(
    // 🔨 The pause before a search runs can outlast the read, so the answer is asked at call time and not from the render that built this function.
    // 🔨 T3597: a parked search that is aborted leaves the queue and rejects, so a closed or retyped box neither runs late nor holds its promise.
    () => (query, signal) => {
      if (arrived.current) return latest.current(query, signal);
      return new Promise<SuggestionRow[]>((resolve, reject) => {
        const release = () => {
          signal.removeEventListener('abort', cancel);
          resolve(latest.current(query, signal));
        };
        const cancel = () => {
          waiting.current = waiting.current.filter((parked) => parked !== release);
          reject(new DOMException('The search was aborted', 'AbortError'));
        };
        if (signal.aborted) return cancel();
        signal.addEventListener('abort', cancel, { once: true });
        waiting.current.push(release);
      });
    },
    [],
  );
  const held = typeof value === 'string' && value ? value : null;
  const shown = held ? (picked ?? rows.find((row) => row.value === held)?.label ?? (valueLabel || null)) : null;
  const choose = (row: SuggestionRow) => {
    setPicked(row.id ? row.line1 : null);
    onChange(row.id || null);
  };
  return (
    <div className={styles.field} {...dataAttribute}>
      <Dropdown
        size={Size.Medium}
        label={label}
        entityLabel={t('siteModal.siteTypeEntity')}
        search={searchWhenRead}
        minChars={0}
        value={shown}
        placeholder={t('edit.none')}
        onSelect={choose}
        // 🔨 A saved type the answer names no id for keeps the previous pick rather than falling to None; the list is read again, so the new type is offered.
        onAddNew={answer?.mayAdd === true ? () => addNew.open({ kind: 'siteType' }, (row) => { read.reload(); if (row.id) choose(row); }) : null}
      />
      {addNew.host}
    </div>
  );
}
